import React, {
  ReactElement, useMemo, useState, useCallback,
} from 'react'
import {
  Row, Col, Card, Form, Table, Button,
} from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import moment from 'moment'
import { IBroadcastFormRecipientResponseReportRow } from 'types/BroadcastForm'
import { downloadCSV } from 'utils/csvExport'

interface EnterpriseSurveyReportTrendsProps {
  filteredRecipients: IBroadcastFormRecipientResponseReportRow[]
}

type ComparisonPeriod = 'month' | 'quarter' | 'year' | 'allTime'
type Metric = 'satisfaction' | 'responseRate'
type CompareBy = 'community' | 'serviceLevel' | 'role' | 'category' | 'overall'

interface TrendDataPoint {
  period: string
  [key: string]: string | number
}

export const EnterpriseSurveyReportTrends: React.FC<EnterpriseSurveyReportTrendsProps> = ({
  filteredRecipients,
}): ReactElement => {
  const [comparisonPeriod, setComparisonPeriod] = useState<ComparisonPeriod>('month')
  const [metric, setMetric] = useState<Metric>('satisfaction')
  const [compareBy, setCompareBy] = useState<CompareBy>('community')

  // Helper to get the period key from a date
  const getPeriodKey = useCallback((date: Date): string => {
    if (comparisonPeriod === 'allTime') {
      return 'All Time'
    }
    const m = moment(date)
    if (comparisonPeriod === 'month') {
      return m.format('YYYY-MM')
    }
    if (comparisonPeriod === 'quarter') {
      const quarter = Math.floor(m.month() / 3) + 1
      return `${m.year()}-Q${quarter}`
    }
    return m.format('YYYY')
  }, [comparisonPeriod])

  // Helper to format period label for display
  const formatPeriodLabel = useCallback((periodKey: string): string => {
    if (periodKey === 'All Time' || comparisonPeriod === 'allTime') {
      return 'All Time'
    }
    if (comparisonPeriod === 'month') {
      return moment(periodKey, 'YYYY-MM').format('MMM YYYY')
    }
    if (comparisonPeriod === 'quarter') {
      return periodKey.replace('Q', ' Q')
    }
    return periodKey
  }, [comparisonPeriod])

  // Helper to get the compare-by value from a row
  const getCompareByValue = useCallback((row: IBroadcastFormRecipientResponseReportRow): string => {
    if (compareBy === 'overall') {
      return 'Overall'
    }
    if (compareBy === 'community') {
      return row.Account_Name || `Account ${row.AccountId}` || 'Unknown'
    }
    if (compareBy === 'serviceLevel') {
      return row.ServiceLevel || 'Unknown'
    }
    if (compareBy === 'role') {
      return row.JobRole || 'Unknown'
    }
    if (compareBy === 'category') {
      return row.QuestionCategory || 'Unknown'
    }
    return 'Unknown'
  }, [compareBy])

  // Process data for the chart
  const chartData = useMemo(() => {
    // For response rate, we need to work at the recipient level (unique FormSessionId)
    // For satisfaction, we work at the response level (rows with SentimentValue)

    if (metric === 'responseRate') {
      // For response rate, we need to work at the recipient level (unique FormSessionId)
      // Group by period and compare-by value, tracking unique sessions
      const periodMap = new Map<string, Map<string, { totalSessions: Set<string>, respondedSessions: Set<string> }>>()

      filteredRecipients.forEach((row) => {
        // Use CreatedAtUtc for when survey was sent (not ResponseAtUtc)
        // Response rate is about whether recipients responded, not when they responded
        if (!row.CreatedAtUtc) return

        const date = new Date(row.CreatedAtUtc)
        const periodKey = getPeriodKey(date)
        const compareValue = getCompareByValue(row)

        if (!periodMap.has(periodKey)) {
          periodMap.set(periodKey, new Map())
        }

        const compareMap = periodMap.get(periodKey)
        if (!compareMap) return

        if (!compareMap.has(compareValue)) {
          compareMap.set(compareValue, { totalSessions: new Set(), respondedSessions: new Set() })
        }

        const stats = compareMap.get(compareValue)
        if (!stats) return

        // Track unique sessions
        stats.totalSessions.add(row.FormSessionId)

        // Track sessions with responses
        if (row.HasResponse) {
          stats.respondedSessions.add(row.FormSessionId)
        }
      })

      // Convert to chart data format
      const periods = Array.from(periodMap.keys()).sort()
      const allCompareValues = new Set<string>()

      periodMap.forEach((compareMap) => {
        compareMap.forEach((_, compareValue) => {
          allCompareValues.add(compareValue)
        })
      })

      return periods.map((periodKey) => {
        const dataPoint: TrendDataPoint = {
          period: formatPeriodLabel(periodKey),
        }

        const compareMap = periodMap.get(periodKey)
        if (compareMap) {
          allCompareValues.forEach((compareValue) => {
            const stats = compareMap.get(compareValue)
            if (stats) {
              const total = stats.totalSessions.size
              const responded = stats.respondedSessions.size
              const rate = total > 0 ? (responded / total) * 100 : 0
              dataPoint[compareValue] = Math.round(rate * 100) / 100 // Round to 2 decimals
            } else {
              dataPoint[compareValue] = 0
            }
          })
        }

        return dataPoint
      })
    }

    // Satisfaction metric: average SentimentValue per period and compare-by value
    const periodMap = new Map<string, Map<string, { sum: number, count: number }>>()

    filteredRecipients.forEach((row) => {
      if (!row.ResponseAtUtc) return

      // Only include rows with sentiment values
      const rowWithSentiment = row as any
      if (rowWithSentiment.SentimentValue == null) return

      const date = new Date(row.ResponseAtUtc)
      const periodKey = getPeriodKey(date)
      const compareValue = getCompareByValue(row)

      if (!periodMap.has(periodKey)) {
        periodMap.set(periodKey, new Map())
      }

      const compareMap = periodMap.get(periodKey)
      if (!compareMap) return

      if (!compareMap.has(compareValue)) {
        compareMap.set(compareValue, { sum: 0, count: 0 })
      }

      const stats = compareMap.get(compareValue)
      if (!stats) return

      stats.sum += rowWithSentiment.SentimentValue
      stats.count += 1
    })

    // Convert to chart data format
    const periods = Array.from(periodMap.keys()).sort()
    const allCompareValues = new Set<string>()

    periodMap.forEach((compareMap) => {
      compareMap.forEach((_, compareValue) => {
        allCompareValues.add(compareValue)
      })
    })

    return periods.map((periodKey) => {
      const dataPoint: TrendDataPoint = {
        period: formatPeriodLabel(periodKey),
      }

      const compareMap = periodMap.get(periodKey)
      if (compareMap) {
        allCompareValues.forEach((compareValue) => {
          const stats = compareMap.get(compareValue)
          if (stats && stats.count > 0) {
            const avg = (stats.sum / stats.count) * 100 // Convert to percentage (0-100)
            dataPoint[compareValue] = Math.round(avg * 100) / 100 // Round to 2 decimals
          } else {
            // Use null to represent no data (will be displayed as N/A in table)
            dataPoint[compareValue] = null
          }
        })
      }

      return dataPoint
    })
  }, [filteredRecipients, metric, getPeriodKey, formatPeriodLabel, getCompareByValue])

  // Get unique compare-by values for legend
  const compareValues = useMemo(() => {
    const values = new Set<string>()
    chartData.forEach((point) => {
      Object.keys(point).forEach((key) => {
        if (key !== 'period') {
          values.add(key)
        }
      })
    })
    return Array.from(values).sort()
  }, [chartData])

  // Calculate totals for table
  const tableTotals = useMemo(() => {
    // Row totals: average of all compare-by values for each period
    // Only includes non-null values in the calculation
    const rowTotals = chartData.map((point) => {
      let sum = 0
      let count = 0
      compareValues.forEach((value) => {
        const val = point[value] as number | null
        if (val != null && !Number.isNaN(val)) {
          sum += val
          count += 1
        }
      })
      return count > 0 ? Math.round((sum / count) * 100) / 100 : null
    })

    // Column totals: average across all periods for each compare-by value
    // Only includes non-null values in the calculation
    const columnTotals: Record<string, number | null> = {}
    compareValues.forEach((value) => {
      let sum = 0
      let count = 0
      chartData.forEach((point) => {
        const val = point[value] as number | null
        if (val != null && !Number.isNaN(val)) {
          sum += val
          count += 1
        }
      })
      columnTotals[value] = count > 0 ? Math.round((sum / count) * 100) / 100 : null
    })

    // Grand total: average of all values
    // Only includes non-null values in the calculation
    let grandTotalSum = 0
    let grandTotalCount = 0
    chartData.forEach((point) => {
      compareValues.forEach((value) => {
        const val = point[value] as number | null
        if (val != null && !Number.isNaN(val)) {
          grandTotalSum += val
          grandTotalCount += 1
        }
      })
    })
    const grandTotal = grandTotalCount > 0
      ? Math.round((grandTotalSum / grandTotalCount) * 100) / 100
      : null

    return {
      rowTotals,
      columnTotals,
      grandTotal,
    }
  }, [chartData, compareValues])

  // Color palette for bars
  const COLORS = [
    '#3b82f6', // blue
    '#10b981', // emerald
    '#f59e0b', // amber
    '#ef4444', // red
    '#8b5cf6', // violet
    '#ec4899', // pink
    '#06b6d4', // cyan
    '#84cc16', // lime
    '#f97316', // orange
    '#6366f1', // indigo
  ]

  return (
    <div>
      <Card className="mb-4 mt-3">
        <Card.Body>
          <Row>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Comparison Period</Form.Label>
                <Form.Control
                  as="select"
                  value={comparisonPeriod}
                  onChange={(e) => setComparisonPeriod(e.target.value as ComparisonPeriod)}
                >
                  <option value="month">By Month</option>
                  <option value="quarter">By Quarter</option>
                  <option value="year">By Year</option>
                  <option value="allTime">All Time</option>
                </Form.Control>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Metric</Form.Label>
                <Form.Control
                  as="select"
                  value={metric}
                  onChange={(e) => setMetric(e.target.value as Metric)}
                >
                  <option value="satisfaction">Satisfaction</option>
                  <option value="responseRate">Response Rate</option>
                </Form.Control>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Compare By</Form.Label>
                <Form.Control
                  as="select"
                  value={compareBy}
                  onChange={(e) => setCompareBy(e.target.value as CompareBy)}
                >
                  <option value="community">Community</option>
                  <option value="serviceLevel">Service Level</option>
                  <option value="role">Role</option>
                  <option value="category">Category</option>
                  <option value="overall">Overall</option>
                </Form.Control>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {chartData.length === 0 ? (
        <Card>
          <Card.Body>
            <div className="text-center text-muted py-4">
              No data available for the selected filters.
            </div>
          </Card.Body>
        </Card>
      ) : (
        <Card>
          <Card.Body>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart
                data={chartData}
                margin={{
                  top: 20, right: 30, left: 20, bottom: 60,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="period"
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  style={{ fontSize: '12px' }}
                />
                <YAxis
                  label={{
                    value: metric === 'satisfaction' ? 'Satisfaction (%)' : 'Response Rate (%)',
                    angle: -90,
                    position: 'insideLeft',
                  }}
                  domain={[0, 100]}
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip
                  formatter={(value: number) => [
                    `${Math.round(value * 100) / 100}%`,
                    metric === 'satisfaction' ? 'Satisfaction' : 'Response Rate',
                  ]}
                  labelFormatter={(label) => `Period: ${label}`}
                />
                <Legend />
                {compareValues.map((value, index) => (
                  <Bar
                    key={value}
                    dataKey={value}
                    fill={COLORS[index % COLORS.length]}
                    name={value}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Card.Body>
        </Card>
      )}

      {chartData.length > 0 && (
        <Card className="mt-4">
          <Card.Header>
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Trends Data Table</h5>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => {
                  const headers = ['Period', ...compareValues, 'Total']
                  const rows = chartData.map((point, index) => [
                    point.period,
                    ...compareValues.map((value) => {
                      const val = point[value] as number | null
                      return val != null && !Number.isNaN(val) ? `${Math.round(val * 100) / 100}%` : 'N/A'
                    }),
                    tableTotals.rowTotals[index] != null
                      ? `${tableTotals.rowTotals[index]}%`
                      : 'N/A',
                  ])
                  // Add totals row
                  rows.push([
                    'Total',
                    ...compareValues.map((value) => (tableTotals.columnTotals[value] != null
                      ? `${tableTotals.columnTotals[value]}%`
                      : 'N/A')),
                    tableTotals.grandTotal != null ? `${tableTotals.grandTotal}%` : 'N/A',
                  ])
                  downloadCSV(headers, rows, 'trends')
                }}
                disabled={chartData.length === 0}
              >
                <FontAwesomeIcon icon="download" className="mr-2" />
                Download CSV
              </Button>
            </div>
          </Card.Header>
          <Card.Body>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Period</th>
                  {compareValues.map((value) => (
                    <th key={value}>{value}</th>
                  ))}
                  <th className="font-weight-bold">Total</th>
                </tr>
              </thead>
              <tbody>
                {chartData.map((point, index) => (
                  <tr key={point.period}>
                    <td className="font-weight-bold">{point.period}</td>
                    {compareValues.map((value) => {
                      const val = point[value] as number | null
                      return (
                        <td key={value}>
                          {val != null && !Number.isNaN(val) ? `${Math.round(val * 100) / 100}%` : 'N/A'}
                        </td>
                      )
                    })}
                    <td className="font-weight-bold">
                      {tableTotals.rowTotals[index] != null
                        ? `${tableTotals.rowTotals[index]}%`
                        : 'N/A'}
                    </td>
                  </tr>
                ))}
                <tr className="font-weight-bold bg-light">
                  <td>Total</td>
                  {compareValues.map((value) => (
                    <td key={value}>
                      {tableTotals.columnTotals[value] != null
                        ? `${tableTotals.columnTotals[value]}%`
                        : 'N/A'}
                    </td>
                  ))}
                  <td>
                    {tableTotals.grandTotal != null ? `${tableTotals.grandTotal}%` : 'N/A'}
                  </td>
                </tr>
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}
    </div>
  )
}
